import {css} from '@emotion/react'
import {colors, gethomeLink} from '../../../constants'

import StartpageLogo from '../../../images/startpage-logo-new.svg'
import AttentionArrow from '../../../images/browser/attention-arrow.svg'
import XButton from '../../../images/x-button.svg'
import {isChrome} from '../../../util/userAgent'
import {createMarkup} from '../../../util/datoCmsDataFactory'

const text = {
    en: {
        headerText: `Let's do this!`,
        bodyText: `Follow these steps to start searching and browsing in private.`,
        stepsData: [
            {
                title: `1. Download`,
                description: `When the window appears, click “Save” and allow the file to download.`,
            },
            {
                title: `2. Install`,
                description: `Open the file at the bottom of this window, and follow the instructions to install Startpage.`,
            },
            {
                title: `3. Customize`,
                description: `Select your preferred search engine and import bookmarks from your previous browser.`,
            },
        ],
        footerText: `<p>If your download doesn’t start automatically,<a href="#">click here.</a></p>`,
        tipText: `Open the file to install Startpage`,
    },
    de: {
        headerText: `Los geht's!`,
        bodyText: `Befolge die folgenden Schritte für privates Suchen und Surfen.`,
        stepsData: [
            {
                title: `1. Herunterladen`,
                description: `Speichere im nächsten Schritt die Installationsdatei auf deinen Computer`,
            },
            {
                title: `2. Installieren`,
                description: `Öffne die Datei, die auf dieser Seite unten angezeigt wird und befolge die Anweisungen zur Installation von Startpage.`,
            },
            {
                title: `3. Personalisieren`,
                description: `Wähle deine Standardsuchmaschine aus und importiere deine Lesezeichen aus deinem bisherigen Browser.`,
            },
        ],
        footerText: `<p>Wenn der Download nicht automatisch beginnt, <a href="#">klicke bitte hier.</a></p>`,
        tipText: `Öffne die Datei, um Startpage zu installieren`,
    },
    nl: {
        headerText: `Laten we dit doen!`,
        bodyText: `Volg deze stappen om privé te gaan zoeken en browsen.`,
        stepsData: [
            {
                title: `1. Downloaden`,
                description: `Wanneer het venster verschijnt, klik je op "Opslaan" om het bestand te downloaden.`,
            },
            {
                title: `2. Installeren`,
                description: `Open het bestand onderaan dit venster, en volg de instructies om Startpage te installeren.`,
            },
            {
                title: `3. Personaliseren`,
                description: `Selecteer je favoriete zoekmachine en importeer bladwijzers uit je vorige browser.`,
            },
        ],
        footerText: `<p>Als je download niet automatisch start, <a href="#">klik dan hier.</a></p>`,
        tipText: `Open het bestand om Startpage te installeren`,
    },
}

const SuccessModal = ({show = false, languageCode}) => {
    const onXButtonClick = () => {
        const event = new Event('hideSuccessModal')
        window.dispatchEvent(event)
    }

    const {headerText, bodyText, stepsData, footerText, tipText} = text[languageCode]

    return (
        <div
            className="success-modal"
            css={css`
                display: ${show ? 'block' : 'none'};
                position: fixed;
                top: 0;
                left: 0;
                background: rgba(0, 0, 0, 0.5);
                padding: 20px;
                height: 100vh;
                width: 100vw;
                z-index: 9999;
            `}
        >
            <div
                className="modal"
                css={css`
                    background: ${colors.whisperLight};
                    border-radius: 10px;
                    height: 100%;
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    position: relative;
                    padding-top: 80px;
                    overflow: hidden;
                `}
            >
                <button
                    css={css`
                        border: none;
                        background: transparent;
                        padding: 0;
                        position: absolute;
                        top: 40px;
                        right: 40px;
                        &:hover {
                            cursor: pointer;
                        }
                    `}
                    onClick={onXButtonClick}
                    type="button"
                >
                    <XButton />
                </button>
                <a
                    href={gethomeLink()}
                    css={css`
                        transform: scale(1.3);
                        @media (max-height: 875px) {
                            margin-bottom: 40px;
                        }
                    `}
                >
                    <StartpageLogo />
                </a>
                <div className="body">
                    <div
                        className="intro-text"
                        css={css`
                            margin: 0 2rem;
                            h2 {
                                font-size: 64px;
                                font-weight: 800;
                                margin-bottom: 16px;
                            }
                        `}
                    >
                        <h2>{headerText}</h2>
                        <p>{bodyText}</p>
                    </div>
                    <div
                        className="steps"
                        css={css`
                            display: grid;
                            grid-template-columns: 1fr 1fr 1fr;
                            grid-gap: 20px;
                            max-width: 900px;
                            margin: 80px auto 80px;
                            padding: 0 2rem;
                        `}
                    >
                        {stepsData.map(({title, description}, i) => {
                            return (
                                <div
                                    className="step"
                                    key={i}
                                    css={css`
                                        border-right: 2px solid ${colors.whisperDark};
                                        text-align: left;
                                        padding-right: 20px;
                                        &:nth-last-of-type(1) {
                                            border: none;
                                        }
                                        .de-text {
                                            font-size: 27px;
                                        }
                                        .nl-text {
                                            font-size: 28px;
                                        }
                                        .en-text {
                                            font-size: 30px;
                                        }
                                        h3 {
                                            color: ${colors.brandBlue};
                                            font-weight: 800;
                                            margin-bottom: 16px;
                                        }
                                        p {
                                            line-height: 24px;
                                        }
                                    `}
                                >
                                    <h3 className={`${languageCode}-text`}>{title}</h3>
                                    <p>{description}</p>
                                </div>
                            )
                        })}
                    </div>
                    <p
                        css={css`
                            a {
                                color: ${colors.brandBlue};
                                text-decoration: underline;
                            }
                        `}
                        dangerouslySetInnerHTML={createMarkup(footerText)}
                    />
                </div>

                <div
                    className="bottom-gradient"
                    css={css`
                        background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTM0NCIgaGVpZ2h0PSIyMzIiIHZpZXdCb3g9IjAgMCAxMzQ0IDIzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xMzQ0IDU0LjI2NTRWMjIyQzEzNDQgMjI3LjUyMyAxMzM5LjUyIDIzMiAxMzM0IDIzMkg5Ljk5OTk2QzQuNDc3MTEgMjMyIDAgMjI3LjUyMyAwIDIyMlYyNi42NTQyQzIyMS4yNjMgLTI1Ljk3IDQzNi4xNDQgMTEuNTE3MSA2NDkuNzM3IDQ4Ljc3OTdDNzY2LjkzMiA2OS4yMjUgODgzLjczOSA4OS42MDI2IDEwMDEgOTQuOTkxQzExMjAuNzUgMTAwLjQ5NCAxMjUzLjg5IDc3Ljc4MjggMTM0NCA1NC4yNjU0WiIgZmlsbD0idXJsKCNwYWludDBfbGluZWFyXzY1OF8xNTI1NCkiLz4KPGRlZnM+CjxsaW5lYXJHcmFkaWVudCBpZD0icGFpbnQwX2xpbmVhcl82NThfMTUyNTQiIHgxPSIxOTMiIHkxPSItNTIuOTk5OCIgeDI9IjEzMjEiIHkyPSI5MjguOTkxIiBncmFkaWVudFVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+CjxzdG9wIHN0b3AtY29sb3I9IiM2OTdBRkMiLz4KPHN0b3Agb2Zmc2V0PSIwLjc0NDM2MSIgc3RvcC1jb2xvcj0iIzI5RERDQyIvPgo8L2xpbmVhckdyYWRpZW50Pgo8L2RlZnM+Cjwvc3ZnPgo=);
                        background-position: 50% 0%;
                        background-repeat: no-repeat;
                        background-size: cover;
                        padding: 6rem 2rem 5rem;
                        height: 100px;
                        width: 100%;
                    `}
                />
                {isChrome() && (
                    <div
                        className="attention"
                        css={css`
                            position: absolute;
                            bottom: 10px;
                            left: 50px;
                            display: flex;
                            flex-direction: column;
                            p {
                                color: ${colors.whisperLight};
                                margin-bottom: 5px;
                            }
                            @media (max-height: 760px) {
                                display: none;
                            }
                            @media (max-width: 940px) {
                                display: none;
                            }
                            @media (min-width: 2075px) {
                                display: none;
                            }
                        `}
                    >
                        <p className="prompt">{tipText}</p>
                        <AttentionArrow />
                    </div>
                )}
            </div>
        </div>
    )
}

export default SuccessModal
